import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleMedListParents() {
  return (
    <ArticleLayout
      meta={{
        slug: "medication-list-aging-parents",
        hub: "Family & Legacy",
        time: "6 min read",
        title: "Medication List Template for Aging Parents (Free, Printable) | Centennial",
        seoDescription:
          "How to build a complete medication list for an aging parent: the 7 fields every entry needs, look-alike drug warnings, wallet and caregiver copies, and a free private organizer.",
        h1: "A Medication List for Aging Parents",
        hook: "In an emergency room, the first question is always the same: 'What medications do they take?' Most families can't answer it completely. Fifteen minutes fixes that.",
        quickAnswer:
          "Every aging parent needs a written medication list with 7 fields per entry: medication name (brand and generic), strength, dose instructions, prescribing doctor, reason for taking it, pharmacy, and known allergies at the top. Keep three copies — one on the fridge, one wallet card, one with the primary caregiver — and update it at every doctor visit. A free private organizer that prints all three formats is linked below.",
      }}
    >
      <H2>Why this list matters more than almost any document</H2>
      <p>
        Adults over 65 fill an average of 14+ prescriptions per year, and many take five or more
        medications daily. Every hospital admission, new specialist, and pharmacy switch is a
        chance for a dangerous interaction or duplication — and the person least able to recite
        the full list is often the patient in that moment. A current, complete list is the
        single cheapest piece of safety equipment a family can own.
      </p>

      <H2>The 7 fields every entry needs</H2>
      <DataTable
        head={["Field", "Example", "Why it matters"]}
        rows={[
          ["1. Name (brand + generic)", "Lipitor / atorvastatin", "Prevents double-dosing under two names"],
          ["2. Strength", "20 mg", "Same drug comes in many strengths"],
          ["3. Instructions", "1 tablet at bedtime", "Timing and food rules matter"],
          ["4. Prescriber", "Dr. Alvarez (cardiology)", "Who to call with questions"],
          ["5. Purpose", "Cholesterol", "Catches duplicate treatment across doctors"],
          ["6. Pharmacy", "Main St. Pharmacy", "One pharmacy = interaction checks work"],
          ["7. Start date / last change", "Changed 3/2026", "Flags what's new after a hospital stay"],
        ]}
      />
      <p>
        Put <strong>allergies and bad reactions at the very top</strong> in large print, plus
        supplements and over-the-counter items — they interact too.
      </p>

      <H2>Watch for look-alike, sound-alike drugs</H2>
      <p>
        Many medication errors involve names that look or sound similar (hydroxyzine vs
        hydralazine, clonidine vs clonazepam). When a refill suddenly looks different — new
        color, new shape — don't assume it's a generic swap. Check the label's five points
        (name, strength, imprint, shape, manufacturer) and call the pharmacist if anything is
        off. Never identify a pill by color or shape alone.
      </p>

      <H2>Three copies, three places</H2>
      <p>
        <strong>Fridge copy</strong>: EMTs are trained to look there. <strong>Wallet
        card</strong>: travels to every appointment. <strong>Caregiver copy</strong>: whoever
        would answer the 2 a.m. phone call needs the current list, not last year's. Review at
        every doctor visit and after any hospital stay — discharge medication changes are where
        lists most often go stale.
      </p>

      <H2>A free, private way to do all of this</H2>
      <p>
        Centennial's free{" "}
        <Link className="text-[#2B5339] underline" to="/medication-safety">Medication Safety Card</Link>{" "}
        builds the complete list with all seven fields, flags common look-alike drugs
        automatically, and prints the weekly card, wallet list, and caregiver copy — in large
        print. Everything stays in your browser; nothing is uploaded to any server. It's
        organization and education only — your pharmacist is always the final word.
      </p>
      <p>
        While you're organizing for your parents, take ten minutes for yourself: the{" "}
        <Link className="text-[#2B5339] underline" to="/learn/readiness-workbook">readiness workbook</Link>{" "}
        gathers your own accounts, documents, and wishes into one place your family can find.
      </p>
    </ArticleLayout>
  );
}
